// Default endurance and coordination workouts: runs, rides, a swim, rowing, a hike, and short coordination,
// balance and agility sessions. Our own plans. Distance blocks carry a time amount at the target pace, so
// today's session player runs them on a clock; the cardio player (build step 3) ends them on the distance.
// The easy lead-in and easy finish of a run or ride are part of the main work; the warm-up and cool-down are
// the small routines (warmupId, cooldownId, 2026-10-14), which can be switched or turned off.
import { CARDIO_SPORTS } from '../cardio/sports';
import type { Block, CardioSport, CardioTarget, Workout } from '../types';
import { ids, t } from './plan';

type Draft = Omit<Block, 'id'>;

/** A block of a distance at a pace (s per km; the sport's typical speed when no pace is given). */
function dist(exerciseId: string, meters: number, target: Omit<CardioTarget, 'distanceM'> & { sport?: CardioSport } = {}, note?: string): Draft {
  const { sport, ...rest } = target;
  const pace = rest.paceSecPerKm ?? (rest.speedKmh ? 3600 / rest.speedKmh : 3600 / CARDIO_SPORTS[sport ?? 'running'].typicalKmh);
  return { exerciseId, amount: t(Math.round((meters / 1000) * pace)), cardio: { distanceM: meters, ...rest }, ...(note ? { note } : {}) };
}

/** A block of a time at a target. */
function timed(exerciseId: string, seconds: number, target: Omit<CardioTarget, 'durationSec'> = {}, note?: string): Draft {
  return { exerciseId, amount: t(seconds), cardio: { durationSec: seconds, ...target }, ...(note ? { note } : {}) };
}

const easy = (zone: 1 | 2 = 2): CardioTarget => ({ role: 'easy', zone });
const jog = (seconds: number): Draft => timed('run', seconds, { role: 'recovery', recovery: 'jog' });
const OWN = { label: 'Our own' };

export const CARDIO_WORKOUTS: Workout[] = [
  {
    id: 'easy_run_30',
    name: 'Easy Run 30',
    description: 'Thirty minutes of easy running in zone 2, where you can still talk. The base of every running plan.',
    level: 'beginner', focus: 'endurance', activity: 'cardio', sport: 'running', equipment: ['none'], xp: 45, source: OWN, warmupId: 'running_drills', cooldownId: 'runners',
    sections: [
      { id: 'easy_start', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'walking' }, blocks: ids('easy_start', [timed('walk', 180, easy(1), 'brisk walk')]) },
      { id: 'main', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'running' }, blocks: ids('main', [timed('run', 1800, easy(2), 'conversation pace')]) },
    ],
  },
  {
    id: 'long_run',
    name: 'Long Run',
    description: 'Seventy-five easy minutes in zone 2. Builds endurance; slow down on hills instead of pushing.',
    level: 'intermediate', focus: 'endurance', activity: 'cardio', sport: 'running', equipment: ['none'], xp: 90, source: OWN, warmupId: 'running_drills', cooldownId: 'runners',
    sections: [
      { id: 'easy_start', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'walking' }, blocks: ids('easy_start', [timed('walk', 180, easy(1))]) },
      { id: 'main', kind: 'main', format: { kind: 'list' }, cardio: { style: 'long', sport: 'running' }, blocks: ids('main', [timed('run', 4500, easy(2), 'drink every 20 to 30 minutes')]) },
    ],
  },
  {
    id: 'intervals_6x800',
    name: '6 x 800 m Intervals',
    description: 'Six fast 800 m repeats at 4:30 min/km with 2 minutes of easy jogging between, on a track or a flat road.',
    level: 'intermediate', focus: 'endurance', activity: 'cardio', sport: 'running', equipment: ['none'], xp: 80, source: OWN, warmupId: 'running_drills', cooldownId: 'runners',
    sections: [
      { id: 'easy_start', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'running' }, blocks: ids('easy_start', [
        timed('run', 600, easy(2)),
        { exerciseId: 'a_skip', amount: t(30) },
        { exerciseId: 'strides', amount: t(20), note: 'build up, then ease off' },
        { exerciseId: 'strides', amount: t(20) },
      ]) },
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 6, workSec: 216, restSec: 0 }, cardio: { style: 'intervals', sport: 'running' }, blocks: ids('main', [
        dist('track_run', 800, { role: 'work', paceSecPerKm: 270, zone: 4 }),
        jog(120),
      ]) },
      { id: 'easy_finish', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'running' }, blocks: ids('easy_finish', [timed('run', 600, easy(1))]) },
    ],
  },
  {
    id: 'tempo_run_20',
    name: 'Tempo Run 20',
    description: 'Twenty minutes comfortably hard (zone 4, effort 7 of 10) between an easy start and finish.',
    level: 'intermediate', focus: 'endurance', activity: 'cardio', sport: 'running', equipment: ['none'], xp: 65, source: OWN, warmupId: 'running_drills', cooldownId: 'runners',
    sections: [
      { id: 'easy_start', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'running' }, blocks: ids('easy_start', [timed('run', 600, easy(2))]) },
      { id: 'main', kind: 'main', format: { kind: 'list' }, cardio: { style: 'tempo', sport: 'running' }, blocks: ids('main', [timed('run', 1200, { role: 'work', zone: 4, rpe: 7 }, 'steady, hard but controlled')]) },
      { id: 'easy_finish', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'running' }, blocks: ids('easy_finish', [timed('run', 600, easy(1))]) },
    ],
  },
  {
    id: 'hill_repeats',
    name: 'Hill Repeats',
    description: 'Eight hard 60 second climbs on a steady hill, walking or jogging back down, between easy running.',
    level: 'intermediate', focus: 'endurance', activity: 'cardio', sport: 'running', equipment: ['none'], xp: 70, source: OWN, warmupId: 'running_drills', cooldownId: 'runners',
    sections: [
      { id: 'easy_start', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'running' }, blocks: ids('easy_start', [timed('run', 720, easy(2), 'run to the hill')]) },
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 8, workSec: 60, restSec: 0 }, cardio: { style: 'hill_repeats', sport: 'running' }, blocks: ids('main', [
        timed('run', 60, { role: 'work', rpe: 9, elevationM: 15 }, 'drive the arms, short quick steps'),
        timed('walk', 90, { role: 'recovery', recovery: 'walk' }, 'back down'),
      ]) },
      { id: 'easy_finish', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'running' }, blocks: ids('easy_finish', [timed('run', 600, easy(1))]) },
    ],
  },
  {
    id: 'zone2_ride_60',
    name: 'Zone 2 Ride 60',
    description: 'An hour on the bike in zone 2: steady, light gears, a cadence around 85 to 95. Outside or on a trainer.',
    level: 'beginner', focus: 'endurance', activity: 'cardio', sport: 'cycling', equipment: ['bicycle'], xp: 70, source: OWN, warmupId: 'legs', cooldownId: 'legs_hips',
    sections: [
      { id: 'easy_start', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'cycling' }, blocks: ids('easy_start', [timed('road_ride', 300, easy(1), 'easy spin')]) },
      { id: 'main', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'cycling' }, blocks: ids('main', [timed('road_ride', 3600, { ...easy(2), cadence: 90 })]) },
      { id: 'easy_finish', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'cycling' }, blocks: ids('easy_finish', [timed('road_ride', 300, easy(1), 'easy spin')]) },
    ],
  },
  {
    id: 'bike_intervals_4x4',
    name: 'Bike Intervals 4 x 4 min',
    description: 'Four very hard 4 minute efforts (zone 5) with 4 easy minutes between. Best on a trainer or a quiet climb.',
    level: 'advanced', focus: 'endurance', activity: 'cardio', sport: 'cycling', equipment: ['bicycle'], xp: 75, source: { label: 'Our own; the 4 x 4 minute format follows the Norwegian 4x4 interval protocol' }, warmupId: 'legs', cooldownId: 'legs_hips',
    sections: [
      { id: 'easy_start', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'cycling' }, blocks: ids('easy_start', [timed('indoor_ride', 900, easy(2), 'build up slowly')]) },
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 4, workSec: 240, restSec: 0 }, cardio: { style: 'intervals', sport: 'cycling' }, blocks: ids('main', [
        timed('indoor_ride', 240, { role: 'work', zone: 5, rpe: 9 }),
        timed('indoor_ride', 240, { role: 'recovery', recovery: 'easy_spin', zone: 1 }),
      ]) },
      { id: 'easy_finish', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'cycling' }, blocks: ids('easy_finish', [timed('indoor_ride', 600, easy(1))]) },
    ],
  },
  {
    id: 'mtb_trail_ride',
    name: 'MTB Trail Ride',
    description: 'Seventy-five minutes on trails with about 600 m of climbing at a steady effort; ride the descents relaxed.',
    level: 'intermediate', focus: 'endurance', activity: 'cardio', sport: 'mountain_biking', equipment: ['bicycle'], xp: 95, source: OWN, warmupId: 'legs', cooldownId: 'legs_hips',
    sections: [
      { id: 'easy_start', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'mountain_biking' }, blocks: ids('easy_start', [timed('mtb_ride', 600, easy(1), 'roll to the trailhead')]) },
      { id: 'main', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'mountain_biking' }, blocks: ids('main', [timed('mtb_ride', 4500, { role: 'work', zone: 3, rpe: 6, elevationM: 600 }, 'trails S1 to S2 or blue')]) },
    ],
  },
  {
    id: 'swim_1500_technique',
    name: 'Swim 1500 m Technique',
    description: '1\'500 m in the pool: easy start, technique lengths, four steady 200s and an easy finish.',
    level: 'intermediate', focus: 'endurance', activity: 'cardio', sport: 'swimming', equipment: ['none'], xp: 60, source: OWN, warmupId: 'upper_body', cooldownId: 'upper_back',
    sections: [
      { id: 'easy_start', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'swimming' }, blocks: ids('easy_start', [dist('pool_swim', 300, { sport: 'swimming', role: 'easy' }, 'mixed strokes')]) },
      { id: 'drills', kind: 'main', format: { kind: 'circuit', rounds: 4, workSec: 75, restSec: 20 }, cardio: { style: 'drills', sport: 'swimming' }, blocks: ids('drills', [
        dist('pool_swim', 50, { sport: 'swimming', role: 'work', rpe: 4 }, 'technique: catch-up or fist swimming'),
      ]) },
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 4, workSec: 220, restSec: 30 }, cardio: { style: 'intervals', sport: 'swimming' }, blocks: ids('main', [
        dist('pool_swim', 200, { role: 'work', paceSecPerKm: 1100, rpe: 6 }, 'steady freestyle'),
      ]) },
      { id: 'easy_finish', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'swimming' }, blocks: ids('easy_finish', [dist('pool_swim', 200, { sport: 'swimming', role: 'easy' }, 'very easy, any stroke')]) },
    ],
  },
  {
    id: 'rowing_5x500',
    name: 'Rowing 5 x 500 m',
    description: 'Five 500 m pieces on the rowing machine at 2:00 min/500 m with 2 easy minutes between.',
    level: 'intermediate', focus: 'endurance', activity: 'cardio', sport: 'rowing', equipment: ['machine'], xp: 55, source: OWN, warmupId: 'general', cooldownId: 'full_body',
    sections: [
      { id: 'easy_start', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'rowing' }, blocks: ids('easy_start', [timed('rowing_machine', 300, easy(2), 'legs, body, arms')]) },
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 5, workSec: 120, restSec: 0 }, cardio: { style: 'intervals', sport: 'rowing' }, blocks: ids('main', [
        dist('rowing_machine', 500, { role: 'work', paceSecPerKm: 240, rpe: 8 }),
        timed('rowing_machine', 120, { role: 'recovery', recovery: 'easy_row' }),
      ]) },
      { id: 'easy_finish', kind: 'main', format: { kind: 'list' }, cardio: { style: 'steady', sport: 'rowing' }, blocks: ids('easy_finish', [
        timed('rowing_machine', 180, easy(1)),
      ]) },
    ],
  },
  {
    id: 'hike_elevation',
    name: 'Hike with Elevation',
    description: 'Ninety minutes uphill and down with about 400 m of climbing, at a pace you can keep talking.',
    level: 'beginner', focus: 'endurance', activity: 'cardio', sport: 'hiking', equipment: ['none'], xp: 95, source: OWN, warmupId: 'legs', cooldownId: 'runners',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'list' }, cardio: { style: 'long', sport: 'hiking' }, blocks: ids('main', [timed('hike', 5400, { role: 'work', zone: 2, elevationM: 400 }, 'short steps uphill')]) },
    ],
  },
  {
    id: 'coordination_15',
    name: 'Coordination 15',
    description: 'Footwork, hops and balance without equipment: carioca, line hops, skips and one-leg stands, two rounds.',
    level: 'beginner', focus: 'coordination', activity: 'coordination', equipment: ['none'], xp: 30, source: OWN, warmupId: 'running_drills', cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 2, workSec: 40, restSec: 20 }, cardio: { style: 'drills' }, blocks: ids('main', [
        { exerciseId: 'carioca', note: '20 s each direction' },
        { exerciseId: 'line_hops' },
        { exerciseId: 'a_skip' },
        { exerciseId: 'single_leg_balance', amount: t(30, true) },
        { exerciseId: 'line_hops_forward' },
        { exerciseId: 'skaters' },
        { exerciseId: 'single_leg_balance_eyes_closed', amount: t(20, true) },
        { exerciseId: 'fast_feet' },
      ]) },
    ],
  },
  {
    id: 'balance_10',
    name: 'Balance 10',
    description: 'Ten quiet minutes for ankles, hips and balance: one-leg stands, eyes closed, airplanes and single-leg hinges.',
    level: 'beginner', focus: 'coordination', activity: 'coordination', equipment: ['none'], xp: 15, source: OWN,
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 2, workSec: 45, restSec: 15 }, blocks: ids('main', [
        { exerciseId: 'single_leg_balance', amount: t(45, true) },
        { exerciseId: 'single_leg_balance_eyes_closed', amount: t(30, true) },
        { exerciseId: 'hip_airplane', amount: t(30, true) },
        { exerciseId: 'single_leg_rdl', amount: t(30, true), note: 'slow, no weight' },
      ]) },
    ],
  },
  {
    id: 'agility_ladder_20',
    name: 'Agility Ladder 20',
    description: 'Quick feet on the ladder: in-in-out-out, icky shuffle and lateral runs, three rounds with short rests.',
    level: 'intermediate', focus: 'coordination', activity: 'coordination', equipment: ['agility_ladder'], xp: 40, source: OWN, warmupId: 'running_drills', cooldownId: 'runners',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 3, workSec: 30, restSec: 30, roundRestSec: 60 }, cardio: { style: 'drills' }, blocks: ids('main', [
        { exerciseId: 'ladder_in_in_out_out' },
        { exerciseId: 'ladder_icky_shuffle' },
        { exerciseId: 'ladder_lateral', note: 'lead with the left, then the right' },
        { exerciseId: 'fast_feet' },
        { exerciseId: 'lateral_shuffle' },
      ]) },
    ],
  },
  {
    id: 'runners_drills_warmup',
    name: 'Runner\'s Drills Warm-up',
    description: 'Ten minutes before a run or race: easy jog, A- and B-skips, butt kicks, carioca and four strides.',
    level: 'beginner', focus: 'coordination', activity: 'coordination', sport: 'running', equipment: ['none'], xp: 20, source: OWN,
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'list' }, cardio: { style: 'drills', sport: 'running' }, blocks: ids('main', [
        timed('run', 300, easy(1), 'very easy jog'),
        { exerciseId: 'a_skip', amount: t(30) },
        { exerciseId: 'b_skip', amount: t(30) },
        { exerciseId: 'butt_kicks', amount: t(30) },
        { exerciseId: 'carioca', amount: t(30) },
      ]) },
      { id: 'strides', kind: 'main', format: { kind: 'circuit', rounds: 4, workSec: 20, restSec: 40 }, cardio: { style: 'drills', sport: 'running' }, blocks: ids('strides', [
        { exerciseId: 'strides', note: 'walk back between' },
      ]) },
    ],
  },
];
